/**
 * Feature flags do cliente (REV-14, invariante 7).
 *
 * Espelha a API de `apps/server/src/flags.ts` de propósito: quem aprendeu a do
 * servidor já sabe esta. O que muda é de onde vem o valor.
 *
 * Ordem de precedência: query string > env do build > padrão.
 * A query string existe porque o teste que importa é em aparelho físico: ligar o
 * HUD no celular NEVER pode exigir rebuild.
 */
export const FLAG_DEFAULTS = {
  /**
   * HUD de performance na tela (REV-16).
   * Desligado por padrão: número em cima do jogo é ferramenta de quem desenvolve,
   * NEVER do jogador. Ligue com `?perfHud=1`.
   */
  perfHud: false,
  /**
   * `window.__GAME__`, o contrato que o E2E interroga (REV-13, ADR-0009).
   * Desligado por padrão, e o padrão é a parte importante: é superfície de
   * observação do estado da partida, e jogo competitivo NEVER a entrega de graça.
   */
  debugApi: false,
  /**
   * Predição e reconciliação do próprio avatar.
   *
   * LIGADA por padrão, ao contrário das outras duas — e é decisão, NEVER descuido.
   * `perfHud` e `debugApi` são ferramentas de quem desenvolve; isto é GAMEPLAY.
   * Nascer desligada faria o E2E e o aparelho real medirem o caminho que jogador
   * nenhum vai jogar. A flag existe para DESLIGAR (`?predict=0`) quando houver
   * suspeita de divergência entre o previsto e o autoritativo.
   */
  predict: true,
  /**
   * Interpolação dos OUTROS jogadores.
   *
   * LIGADA por padrão pela mesma regra do `predict` (ADR-0010): é gameplay, e nascer
   * desligada faria o aparelho real medir o jogo que ninguém vai jogar. A flag existe
   * para DESLIGAR (`?interp=0`) e ver o snapshot cru, que é o que se quer na hora de
   * decidir se um solavanco veio da rede ou da interpolação.
   */
  interp: true,
  /**
   * Partida SOLO simulada no próprio cliente, sem servidor nenhum.
   *
   * Desligada por padrão, e aqui a razão é escopo, NEVER ferramenta: é a primeira
   * fatia da fase 02 (o ímã), e ligar por padrão trocaria em silêncio o jogo que o
   * aparelho real mede — que continua sendo o autoritativo. Ligue com `?solo=1`.
   *
   * Enquanto ela existir, o cliente tem DOIS caminhos: `solo` NEVER conecta, e o
   * outro NEVER magnetiza. Quando o corpo disputado ganhar ADR, os dois se juntam.
   */
  solo: false,
  /**
   * O avatar vira o MODELO do robô em vez do cubo cinza.
   *
   * Desligada por padrão, e a razão é a fase, NEVER a qualidade: a fase 02 é
   * greybox de PROPÓSITO (`ART-DIRECTION`, fim), porque o que ela mede é FEEL, e
   * arte na tela é a coisa mais barata do mundo para contaminar julgamento de
   * feel — "ficou bom" vira opinião sobre o robô, e NEVER sobre o ímã. O modelo
   * existe hoje porque o asset chegou hoje (`ADR-0016`); ligar por padrão é
   * decisão da fase 03. Ligue com `?robot=1`.
   */
  robot: false,
  /**
   * O carimbo de versão no canto da tela.
   *
   * LIGADO por padrão, e é o contrário do `perfHud` de propósito: o HUD é número
   * para quem desenvolve, e o carimbo é a resposta a "estou testando a versão
   * certa?" — pergunta de quem TESTA, e testador não sabe query string de cor.
   * Nascer desligado faria toda sessão de teste começar com uma instrução a mais,
   * e é justamente a instrução que mais se esquece.
   *
   * `deploy.sh` desliga em prod (`VITE_FLAG_BUILD_STAMP=0`): lá o carimbo NEVER é
   * ferramenta, é sujeira em cima do jogo de quem só quer jogar.
   */
  buildStamp: true,
  /**
   * O painel que diz ao jogador que a partida acabou.
   *
   * LIGADO por padrão pela regra do `ADR-0010`: é GAMEPLAY, e nascer desligado
   * devolveria exatamente o bug que ele conserta — o cliente mudo desenhando um
   * mundo congelado a 60 fps enquanto o jogador martela o botão.
   *
   * A flag existe para DESLIGAR (`?matchEnd=0`) e voltar a ver o último estado
   * cru do mundo, que é o que se quer na hora de investigar o que o servidor
   * mandou logo antes de encerrar.
   */
  matchEnd: true,
  /**
   * O avatar ANIMADO: a mesma malha, agora com esqueleto e as animações que o
   * autor gerou no Mixamo.
   *
   * Nasce DESLIGADA, e isto é exceção à regra do `ADR-0010` — que manda flag de
   * gameplay nascer ligada — pelo motivo que o `ADR-0016` já tinha escrito: a
   * fase 02 é greybox de PROPÓSITO. O Portão 02 mede se alguém pede "mais uma", e
   * robô animado na tela NEVER deixa distinguir "mais uma" pelo JOGO de "mais
   * uma" pelo BONECO. Liga em `?robotAnim=1`, e o link da sessão de teste NEVER
   * leva isso.
   *
   * Exclusiva com `robot`: as duas ligadas seriam dois avatares no mesmo lugar.
   * Esta ganha, porque quem a liga pediu explicitamente por ela.
   */
  robotAnim: false,
  /**
   * A FASE 01: a primeira sala desenhada, no lugar da sala de protótipo.
   *
   * Nasce DESLIGADA (mesma exceção da `robotAnim`): a sala cinza é o que a sessão
   * do Portão 02 mede, e trocar o mundo por baixo dela responderia outra
   * pergunta.
   *
   * ⚠️ Precisa estar ligada nos DOIS lados. Aqui ela decide só onde o anel da
   * saída é DESENHADO; quem decide a vitória é o servidor. Ligada só aqui, o
   * jogador vê a saída num canto e vence em outro. Ver `ADR-0021`.
   */
  level01: false,
} as const;

export type FlagName = keyof typeof FLAG_DEFAULTS;

const overrides = new Map<FlagName, boolean>();

/** `perfHud` → `VITE_FLAG_PERF_HUD`. Mesma transformação do servidor, com prefixo do Vite. */
function envKey(name: FlagName): string {
  return `VITE_FLAG_${name.replace(/[A-Z]/g, (c) => `_${c}`).toUpperCase()}`;
}

function parse(raw: unknown): boolean | undefined {
  if (raw === '1' || raw === 'true' || raw === true) return true;
  if (raw === '0' || raw === 'false' || raw === false) return false;
  return undefined;
}

/**
 * Lê as duas fontes. Parâmetros injetáveis para o teste NEVER precisar de browser.
 */
export function loadFlags(
  search: string = location.search,
  env: Record<string, unknown> = import.meta.env,
): void {
  const params = new URLSearchParams(search);
  for (const name of Object.keys(FLAG_DEFAULTS) as FlagName[]) {
    const fromEnv = parse(env[envKey(name)]);
    if (fromEnv !== undefined) overrides.set(name, fromEnv);
    // Query depois do env: quem digitou na barra de endereço quis mais.
    const raw = params.get(name);
    const fromQuery = parse(raw === '' ? '1' : raw);
    if (fromQuery !== undefined) overrides.set(name, fromQuery);
  }
}

export function isEnabled(name: FlagName): boolean {
  return overrides.get(name) ?? FLAG_DEFAULTS[name];
}

/** Só para teste. */
export function setFlag(name: FlagName, value: boolean): void {
  overrides.set(name, value);
}

export function resetFlags(): void {
  overrides.clear();
}

export function snapshotFlags(): Record<FlagName, boolean> {
  const out = {} as Record<FlagName, boolean>;
  for (const name of Object.keys(FLAG_DEFAULTS) as FlagName[]) out[name] = isEnabled(name);
  return out;
}
